import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Home, Send } from "lucide-react";

const PERIOADE = [[7, "7 zile"], [30, "30 zile"], [90, "90 zile"]];

/** Ce s-a refuzat efectiv, într-un loc.
 *
 * Cerut cu motivul lui: „un refuz corect care mă enervează și unul greșit care
 * mă enervează arată identic fără cifra aia". De aceea fiecare cifră vine cu
 * numitorul ei, propoziția de discuție e scrisă de server (o singură dată, nu
 * o dată aici și o dată acolo), iar regula stă lângă cifră — o regulă
 * invizibilă nu supraviețuiește unei săptămâni aglomerate. */
export default function RoutingSettings({ workspaceId }) {
  const [zile, setZile] = useState(30);
  const [date, setDate] = useState(null);
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    if (!workspaceId) return;
    let anulat = false;
    setEroare("");
    api.get("/routing/refuzuri", { params: { workspace_id: workspaceId, zile } })
      .then((r) => { if (!anulat) setDate(r.data); })
      .catch((e) => {
        if (anulat) return;
        setDate(null);
        setEroare(e?.response?.data?.detail || "Nu am putut citi cifrele de rutare");
      });
    return () => { anulat = true; };
  }, [workspaceId, zile]);

  const randuri = date?.randuri || [];
  const categorii = date?.pe_categorie || [];

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="routing-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Ce a rămas acasă</h3>
        <span className="label-tiny ml-2">// RUTAREA DUPĂ CONȚINUT</span>
        <div className="ml-auto flex items-center gap-1">
          {PERIOADE.map(([z, eticheta]) => (
            <button
              key={z} type="button" data-testid={`routing-perioada-${z}`}
              onClick={() => setZile(z)}
              className={`px-2 py-1 text-[11px] border transition-colors ${
                zile === z ? "border-brand/50 text-brand bg-brand/10"
                           : "border-line/20 text-fg/50 hover:text-fg"}`}
            >{eticheta}</button>
          ))}
        </div>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-fg/50 leading-relaxed">
          Când o cerere atinge date care rămân acasă, răspunsul coboară pe modelul local
          și scrie de ce. Aici sunt cifrele pe care se poartă discuția dacă slăbim o
          categorie: fiecare refuz cu numitorul lui, câmpul pe care a bătut și cererea
          care l-a declanșat.
        </p>

        {eroare && (
          <p className="text-xs text-red-400" data-testid="routing-eroare">{eroare}</p>
        )}

        {!date && !eroare && (
          <div className="py-6 text-center label-tiny text-brand">
            se încarcă cifrele<span className="cursor-blink">_</span>
          </div>
        )}

        {date && (
          <div data-testid="routing-total" className="text-sm">
            <span className="font-bold">{date.refuzuri}</span>
            {date.refuzuri === 1 ? " refuz" : " refuzuri"} din{" "}
            <span className="font-bold">{date.total_cereri}</span>
            {date.total_cereri === 1 ? " cerere" : " cereri"} în ultimele {date.zile} de zile.
          </div>
        )}

        {date && categorii.length === 0 && (
          <p className="text-xs text-fg/50" data-testid="routing-gol">
            Nicio decizie înregistrată încă în intervalul ăsta. Cifrele apar pe măsură ce
            se pun întrebări care ating date din spațiu.
          </p>
        )}

        {categorii.map((c, i) => (
          <div key={c.categorie} className="border border-line/10 bg-surface-2/40"
               data-testid={`routing-categorie-${c.categorie}`}>
            <div className="px-4 py-3 flex items-center gap-2 border-b border-line/10">
              {c.verdict === "acasa"
                ? <Home className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                : <Send className="w-3.5 h-3.5 text-fg/40 flex-shrink-0" />}
              {/* Propoziția e scrisă de server, nu recompusă aici: o singură
                  formulare, un singur loc unde se corectează. */}
              <span className="text-sm">{randuri[i] || c.eticheta}</span>
            </div>
            {c.refuzuri > 0 && (
              <div className="px-4 py-3 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {c.campuri.map((cmp) => (
                    <span key={cmp.camp}
                          className="px-2 py-0.5 text-[11px] mono border border-line/20 text-fg/60">
                      {cmp.camp} · {cmp.refuzuri}
                    </span>
                  ))}
                </div>
                {c.cereri.length > 0 && (
                  <ul className="space-y-1">
                    {c.cereri.map((cer, k) => (
                      <li key={k} className="text-xs text-fg/60 leading-relaxed">
                        <span className="text-fg/30 mono">{(cer.model || "")}</span>{" "}
                        „{cer.cerere}"
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}

        {date && (
          <details className="border-t border-line/10 pt-3">
            <summary className="label-tiny cursor-pointer text-fg/50">regula, pe categorii</summary>
            <ul className="mt-3 space-y-2" data-testid="routing-regula">
              {(date.categorii || []).map((c) => (
                <li key={c.cheie} className="text-xs leading-relaxed">
                  <span className={c.verdict === "acasa" ? "text-brand" : "text-fg/70"}>
                    {c.eticheta}
                  </span>
                  <span className="text-fg/30"> — {c.verdict === "acasa" ? "rămâne acasă:" : "poate ieși:"} </span>
                  <span className="text-fg/50">{c.de_ce}</span>
                  {c.nota && <span className="text-fg/40"> {c.nota}</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}
